import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import useProtectedExport from '../hooks/useProtectedExport';
import ExportPasswordDialog from '../components/ExportPasswordDialog';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import { 
  Search, Eye, RefreshCw, AlertCircle, Clock, CheckCircle, 
  XCircle, MessageSquareWarning, Link2, ExternalLink, Copy,
  Plus, Edit, Trash2, Settings, Image, MapPin, Wrench, Download
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const EXPORT_ALLOWED_ROLES = ["Admin"];

const ComplaintsManager = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [complaints, setComplaints] = useState([]);
  const [stats, setStats] = useState({});
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  
  // Check if user can export
  const canExport = EXPORT_ALLOWED_ROLES.includes(user?.role);
  const protectedExport = useProtectedExport();
  
  // Export CSV handler
  const handleExportCSV = async () => {
    try {
      const params = new URLSearchParams();
      if (activeTab !== 'all') params.append('status', activeTab);
      
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/complaints/export/csv?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Export failed');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `complaints_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      toast.success('CSV exported successfully');
    } catch (error) {
      toast.error('Failed to export CSV');
    }
  };
  
  // Dialog states
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [issueTypeDialogOpen, setIssueTypeDialogOpen] = useState(false);
  const [createServiceDialogOpen, setCreateServiceDialogOpen] = useState(false);
  const [selectedComplaint, setSelectedComplaint] = useState(null);
  const [selectedIssueType, setSelectedIssueType] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Service engineers and customers for service creation
  const [engineers, setEngineers] = useState([]);
  const [customers, setCustomers] = useState([]);
  
  // Status update form
  const [statusForm, setStatusForm] = useState({
    status: '',
    service_reference: '',
    cancel_reason: ''
  });

  // Service creation form
  const [serviceForm, setServiceForm] = useState({
    customer_id: '',
    service_type: 'Onsite Field Service',
    issue_description: '',
    priority: 'Medium',
    assigned_to: '',
    due_date: ''
  });

  // Issue type form
  const [issueTypeForm, setIssueTypeForm] = useState({
    name: '',
    is_active: true
  });

  // Get complaint form URL
  const complaintFormUrl = `${window.location.origin}/complaint`;

  const fetchData = useCallback(async () => {
    try {
      const [complaintsRes, statsRes, settingsRes, customersRes] = await Promise.all([
        api.get('/complaints/'),
        api.get('/complaints/stats'),
        api.get('/complaints/settings'),
        api.get('/customers/').catch(() => ({ data: [] }))
      ]);
      
      setComplaints(complaintsRes.data);
      setStats(statsRes.data);
      setSettings(settingsRes.data);
      setCustomers(customersRes.data.filter(c => c.status === 'Active'));
      
      // Fetch engineers using the new assignable endpoint
      try {
        const engineersRes = await api.get('/employees/assignable/services');
        setEngineers(engineersRes.data);
      } catch (e) {
        // Fallback to regular endpoint for Admin
        try {
          const fallbackRes = await api.get('/employees/');
          const engineersList = fallbackRes.data.filter(emp => 
            emp.is_active !== false && 
            (emp.role === 'Service Engineer' || emp.role === 'BDM & SE')
          );
          setEngineers(engineersList);
        } catch (e2) {
          console.error('Failed to fetch engineers:', e2);
        }
      }
    } catch (error) {
      toast.error('Failed to fetch complaints');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSyncServiceStatus = async () => {
    try {
      const response = await api.post('/complaints/sync-service-status');
      if (response.data.updated_count > 0) {
        toast.success(`${response.data.updated_count} complaint(s) auto-completed`);
        fetchData();
      } else {
        toast.info('No complaints to update');
      }
    } catch (error) {
      toast.error('Failed to sync status');
    }
  };

  const handleViewComplaint = (complaint) => {
    setSelectedComplaint(complaint);
    setViewDialogOpen(true);
  };

  const handleOpenStatusUpdate = (complaint) => {
    setSelectedComplaint(complaint);
    setStatusForm({
      status: complaint.status,
      service_reference: complaint.service_reference || '',
      cancel_reason: complaint.cancel_reason || ''
    });
    setStatusDialogOpen(true);
  };

  const handleStatusUpdate = async () => {
    if (!statusForm.status) {
      toast.error('Please select a status');
      return;
    }

    // If changing to "On Process", require service reference OR open create service dialog
    if (statusForm.status === 'On Process' && !statusForm.service_reference) {
      toast.error('Please create a service or enter a service reference number');
      return;
    }

    if (statusForm.status === 'Cancelled' && (!statusForm.cancel_reason || statusForm.cancel_reason.length < 5)) {
      toast.error('Cancel reason is mandatory (min 5 characters)');
      return;
    }

    setSubmitting(true);
    try {
      await api.put(`/complaints/${selectedComplaint.id}/status`, statusForm);
      toast.success('Complaint status updated');
      setStatusDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    } finally {
      setSubmitting(false);
    }
  };

  // Open create service dialog from complaint
  const handleOpenCreateService = (complaint) => {
    setSelectedComplaint(complaint);
    
    // Find customer by mobile number
    const matchingCustomer = customers.find(c => 
      c.mobile === complaint.mobile_number || 
      c.phone === complaint.mobile_number
    );
    
    // Pre-fill service form with complaint data
    setServiceForm({
      customer_id: matchingCustomer?.id || '',
      service_type: 'Onsite Field Service',
      issue_description: `Complaint: ${complaint.complaint_number}\n\nCustomer: ${complaint.customer_name}\nIssue Type: ${complaint.issue_type}\n\nDescription:\n${complaint.message}`,
      priority: 'Medium',
      assigned_to: '',
      due_date: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString().split('T')[0] // 2 days from now
    });
    
    setStatusDialogOpen(false);
    setCreateServiceDialogOpen(true);
  };

  // Create service from complaint
  const handleCreateService = async () => {
    if (!serviceForm.customer_id) {
      toast.error('Please select a customer');
      return;
    }
    if (!serviceForm.issue_description) {
      toast.error('Issue description is required');
      return;
    }

    setSubmitting(true);
    try {
      // Create the service
      const response = await api.post('/services/', {
        customer_id: serviceForm.customer_id,
        service_type: serviceForm.service_type,
        description: serviceForm.issue_description,
        priority: serviceForm.priority,
        assigned_to: serviceForm.assigned_to || null,
        due_date: serviceForm.due_date || null,
        complaint_id: selectedComplaint.id,
        complaint_images: selectedComplaint.photos || []
      });

      const createdService = response.data;
      
      // Update the complaint with service reference and notes
      await api.put(`/complaints/${selectedComplaint.id}/status`, {
        status: 'On Process',
        service_reference: createdService.service_no,
        notes: `Service created: ${createdService.service_no}. View service: /services?id=${createdService.id}`
      });

      toast.success(`Service ${createdService.service_no} created and linked to complaint`);
      setCreateServiceDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create service');
    } finally {
      setSubmitting(false);
    }
  };

  // Settings handlers
  const handleTogglePhotoUpload = async (enabled) => {
    try {
      await api.put('/complaints/settings', { photo_upload_enabled: enabled });
      setSettings(prev => ({ ...prev, photo_upload_enabled: enabled }));
      toast.success(`Photo upload ${enabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      toast.error('Failed to update settings');
    }
  };

  // Issue type handlers
  const handleOpenAddIssueType = () => {
    setSelectedIssueType(null);
    setIssueTypeForm({ name: '', is_active: true });
    setIssueTypeDialogOpen(true);
  };

  const handleOpenEditIssueType = (issueType) => {
    setSelectedIssueType(issueType);
    setIssueTypeForm({ name: issueType.name, is_active: issueType.is_active });
    setIssueTypeDialogOpen(true);
  };

  const handleSaveIssueType = async () => {
    if (!issueTypeForm.name || issueTypeForm.name.trim().length < 2) {
      toast.error('Issue type name must be at least 2 characters');
      return;
    }

    setSubmitting(true);
    try {
      if (selectedIssueType) {
        await api.put(`/complaints/issue-types/${selectedIssueType.id}`, issueTypeForm);
        toast.success('Issue type updated');
      } else {
        await api.post('/complaints/issue-types', issueTypeForm);
        toast.success('Issue type added');
      }
      setIssueTypeDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save issue type');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteIssueType = async (issueTypeId) => {
    if (!window.confirm('Are you sure you want to delete this issue type?')) return;

    try {
      await api.delete(`/complaints/issue-types/${issueTypeId}`);
      toast.success('Issue type deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete issue type');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'New':
        return <Badge className="bg-blue-100 text-blue-800"><AlertCircle className="w-3 h-3 mr-1" />New</Badge>;
      case 'On Process':
        return <Badge className="bg-amber-100 text-amber-800"><Clock className="w-3 h-3 mr-1" />On Process</Badge>;
      case 'Completed':
        return <Badge className="bg-green-100 text-green-800"><CheckCircle className="w-3 h-3 mr-1" />Completed</Badge>;
      case 'Cancelled':
        return <Badge className="bg-red-100 text-red-800"><XCircle className="w-3 h-3 mr-1" />Cancelled</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const filteredComplaints = complaints.filter(c => {
    const matchesSearch = 
      c.complaint_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.mobile_number?.includes(searchTerm) ||
      c.city?.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (activeTab === 'all') return matchesSearch;
    if (activeTab === 'new') return matchesSearch && c.status === 'New';
    if (activeTab === 'process') return matchesSearch && c.status === 'On Process';
    if (activeTab === 'completed') return matchesSearch && c.status === 'Completed';
    if (activeTab === 'cancelled') return matchesSearch && c.status === 'Cancelled';
    return matchesSearch;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Complaints Management</h1>
          <p className="text-gray-600">Manage customer complaints and service requests</p>
        </div>
        <div className="flex gap-2">
          {canExport && (
            <Button variant="outline" onClick={() => protectedExport.requestExport(handleExportCSV)} data-testid="export-csv-button">
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
          )}
          <Button variant="outline" onClick={() => setSettingsDialogOpen(true)}>
            <Settings className="h-4 w-4 mr-2" />
            Settings
          </Button>
          <Button variant="outline" onClick={handleSyncServiceStatus}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Sync Service Status
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-5 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-100 rounded-lg">
                <MessageSquareWarning className="h-5 w-5 text-gray-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total</p>
                <p className="text-xl font-bold">{stats.total || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-blue-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <AlertCircle className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">New</p>
                <p className="text-xl font-bold text-blue-600">{stats.new || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-amber-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Clock className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">On Process</p>
                <p className="text-xl font-bold text-amber-600">{stats.on_process || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-green-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Completed</p>
                <p className="text-xl font-bold text-green-600">{stats.completed || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-red-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <XCircle className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Cancelled</p>
                <p className="text-xl font-bold text-red-600">{stats.cancelled || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Complaint Form URL Card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-blue-800 mb-1">Customer Complaint Form URL</p>
              <p className="text-blue-600 font-mono text-sm">{complaintFormUrl}</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => copyToClipboard(complaintFormUrl)}>
                <Copy className="h-4 w-4 mr-1" />
                Copy
              </Button>
              <Button variant="outline" size="sm" onClick={() => window.open(complaintFormUrl, '_blank')}>
                <ExternalLink className="h-4 w-4 mr-1" />
                Open
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="all">All ({complaints.length})</TabsTrigger>
          <TabsTrigger value="new" className="text-blue-600">New ({stats.new || 0})</TabsTrigger>
          <TabsTrigger value="process">On Process ({stats.on_process || 0})</TabsTrigger>
          <TabsTrigger value="completed">Completed ({stats.completed || 0})</TabsTrigger>
          <TabsTrigger value="cancelled">Cancelled ({stats.cancelled || 0})</TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="space-y-4">
          {/* Search */}
          <Card>
            <CardContent className="pt-4">
              <div className="flex gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search by complaint number, name, mobile, or city..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <Button variant="outline" onClick={fetchData}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Complaints Table */}
          <Card>
            <CardContent className="pt-4">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Complaint #</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>City</TableHead>
                    <TableHead>Issue Type</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Service Ref</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredComplaints.length > 0 ? (
                    filteredComplaints.map((complaint) => (
                      <TableRow key={complaint.id}>
                        <TableCell className="font-mono text-sm font-medium">
                          <div className="flex items-center gap-2">
                            {complaint.complaint_number}
                            {complaint.photo_url && (
                              <Image className="h-4 w-4 text-blue-500" title="Has photo" />
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{complaint.customer_name}</p>
                            <p className="text-xs text-gray-500">{complaint.mobile_number}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <MapPin className="h-3 w-3 text-gray-400" />
                            {complaint.city || '-'}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{complaint.issue_type}</Badge>
                        </TableCell>
                        <TableCell>{getStatusBadge(complaint.status)}</TableCell>
                        <TableCell>
                          {complaint.service_reference ? (
                            <Link 
                              to={`/services?search=${complaint.service_reference}`}
                              className="text-blue-600 hover:underline flex items-center gap-1"
                            >
                              <Link2 className="h-3 w-3" />
                              {complaint.service_reference}
                            </Link>
                          ) : '-'}
                        </TableCell>
                        <TableCell>
                          {new Date(complaint.created_at).toLocaleDateString('en-IN')}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm" onClick={() => handleViewComplaint(complaint)}>
                            <Eye className="h-4 w-4" />
                          </Button>
                          {complaint.status !== 'Completed' && complaint.status !== 'Cancelled' && (
                            <Button size="sm" className="ml-2" onClick={() => handleOpenStatusUpdate(complaint)}>
                              Update
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center text-gray-500 py-8">
                        No complaints found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* View Complaint Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Complaint Details - {selectedComplaint?.complaint_number}</DialogTitle>
          </DialogHeader>
          {selectedComplaint && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500 text-sm">Customer Name</Label>
                  <p className="font-medium">{selectedComplaint.customer_name}</p>
                </div>
                <div>
                  <Label className="text-gray-500 text-sm">Mobile Number</Label>
                  <p className="font-medium">{selectedComplaint.mobile_number}</p>
                </div>
                <div>
                  <Label className="text-gray-500 text-sm">City</Label>
                  <p className="font-medium">{selectedComplaint.city || '-'}</p>
                </div>
                <div>
                  <Label className="text-gray-500 text-sm">Issue Type</Label>
                  <p><Badge variant="outline">{selectedComplaint.issue_type}</Badge></p>
                </div>
                <div>
                  <Label className="text-gray-500 text-sm">Status</Label>
                  <p>{getStatusBadge(selectedComplaint.status)}</p>
                </div>
                <div>
                  <Label className="text-gray-500 text-sm">Created Date</Label>
                  <p className="font-medium">{new Date(selectedComplaint.created_at).toLocaleString('en-IN')}</p>
                </div>
                {selectedComplaint.service_reference && (
                  <div>
                    <Label className="text-gray-500 text-sm">Service Reference</Label>
                    <p className="text-blue-600 font-medium">{selectedComplaint.service_reference}</p>
                  </div>
                )}
              </div>

              {selectedComplaint.notes && (
                <div>
                  <Label className="text-gray-500 text-sm">Notes / Description</Label>
                  <p className="text-sm bg-gray-50 p-3 rounded">{selectedComplaint.notes}</p>
                </div>
              )}

              {selectedComplaint.photo_url && (
                <div>
                  <Label className="text-gray-500 text-sm">Attached Photo</Label>
                  <img 
                    src={`${process.env.REACT_APP_BACKEND_URL}${selectedComplaint.photo_url}`}
                    alt="Complaint" 
                    className="max-h-48 rounded border mt-2 cursor-pointer"
                    onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}${selectedComplaint.photo_url}`, '_blank')}
                  />
                </div>
              )}

              {selectedComplaint.cancel_reason && (
                <div className="bg-red-50 p-3 rounded">
                  <Label className="text-red-600 text-sm">Cancel Reason</Label>
                  <p className="text-sm text-red-700">{selectedComplaint.cancel_reason}</p>
                </div>
              )}

              {selectedComplaint.status_history?.length > 0 && (
                <div>
                  <Label className="text-gray-500 text-sm">Status History</Label>
                  <div className="space-y-2 mt-2 max-h-40 overflow-y-auto">
                    {selectedComplaint.status_history.map((history, idx) => (
                      <div key={idx} className="text-sm p-2 bg-gray-50 rounded flex justify-between">
                        <div>
                          <span className="font-medium">{history.status}</span>
                          {history.note && <span className="text-gray-500 ml-2">- {history.note}</span>}
                        </div>
                        <span className="text-gray-400 text-xs">
                          {new Date(history.timestamp).toLocaleString('en-IN')}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewDialogOpen(false)}>Close</Button>
            {selectedComplaint?.status !== 'Completed' && selectedComplaint?.status !== 'Cancelled' && (
              <Button onClick={() => { setViewDialogOpen(false); handleOpenStatusUpdate(selectedComplaint); }}>
                Update Status
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Status Update Dialog */}
      <Dialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Complaint Status - {selectedComplaint?.complaint_number}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>New Status *</Label>
              <Select
                value={statusForm.status}
                onValueChange={(v) => setStatusForm(prev => ({ ...prev, status: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="New">New</SelectItem>
                  <SelectItem value="On Process">On Process</SelectItem>
                  <SelectItem value="Completed">Completed</SelectItem>
                  <SelectItem value="Cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {statusForm.status === 'On Process' && (
              <div className="space-y-3">
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm font-medium text-blue-800 mb-2">Create or link a service for this complaint</p>
                  <Button 
                    type="button"
                    onClick={() => handleOpenCreateService(selectedComplaint)}
                    className="w-full bg-blue-600 hover:bg-blue-700"
                  >
                    <Wrench className="w-4 h-4 mr-2" />
                    Create Service Request
                  </Button>
                </div>
                
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-white px-2 text-gray-500">Or enter existing</span>
                  </div>
                </div>
                
                <div>
                  <Label>Service Reference Number</Label>
                  <Input
                    value={statusForm.service_reference}
                    onChange={(e) => setStatusForm(prev => ({ ...prev, service_reference: e.target.value }))}
                    placeholder="Enter existing service number (e.g., SRV-00001)"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    This complaint will be linked to the service for status tracking
                  </p>
                </div>
                
                {selectedComplaint?.service_reference && (
                  <div className="p-2 bg-green-50 rounded border border-green-200">
                    <p className="text-sm text-green-800">
                      <CheckCircle className="w-4 h-4 inline mr-1" />
                      Linked to: <Link to={`/services`} className="font-medium underline">{selectedComplaint.service_reference}</Link>
                    </p>
                  </div>
                )}
              </div>
            )}

            {statusForm.status === 'Cancelled' && (
              <div>
                <Label>Cancel Reason *</Label>
                <Textarea
                  value={statusForm.cancel_reason}
                  onChange={(e) => setStatusForm(prev => ({ ...prev, cancel_reason: e.target.value }))}
                  placeholder="Please provide reason for cancellation (min 5 characters)"
                  rows={3}
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleStatusUpdate} disabled={submitting}>
              {submitting ? 'Updating...' : 'Update Status'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog open={settingsDialogOpen} onOpenChange={setSettingsDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Complaint Settings</DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            {/* Photo Upload Toggle */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Image className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="font-medium">Photo Upload</p>
                  <p className="text-sm text-gray-500">Allow customers to attach photos with complaints</p>
                  <p className="text-xs text-gray-400">1 photo (JPG, JPEG, PNG), max 2MB</p>
                </div>
              </div>
              <Switch
                checked={settings?.photo_upload_enabled || false}
                onCheckedChange={handleTogglePhotoUpload}
              />
            </div>

            {/* Issue Types Management */}
            <div>
              <div className="flex justify-between items-center mb-4">
                <div>
                  <p className="font-medium">Issue Types</p>
                  <p className="text-sm text-gray-500">Manage complaint issue types</p>
                </div>
                <Button size="sm" onClick={handleOpenAddIssueType}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add Issue Type
                </Button>
              </div>
              
              <div className="border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Issue Type Name</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {settings?.issue_types?.map((issueType) => (
                      <TableRow key={issueType.id}>
                        <TableCell className="font-medium">{issueType.name}</TableCell>
                        <TableCell>
                          {issueType.is_active ? (
                            <Badge className="bg-green-100 text-green-800">Active</Badge>
                          ) : (
                            <Badge variant="outline" className="text-gray-500">Inactive</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm" onClick={() => handleOpenEditIssueType(issueType)}>
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDeleteIssueType(issueType.id)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSettingsDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Issue Type Add/Edit Dialog */}
      <Dialog open={issueTypeDialogOpen} onOpenChange={setIssueTypeDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{selectedIssueType ? 'Edit Issue Type' : 'Add Issue Type'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Issue Type Name *</Label>
              <Input
                value={issueTypeForm.name}
                onChange={(e) => setIssueTypeForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Enter issue type name"
              />
            </div>
            <div className="flex items-center gap-3">
              <Switch
                checked={issueTypeForm.is_active}
                onCheckedChange={(v) => setIssueTypeForm(prev => ({ ...prev, is_active: v }))}
              />
              <Label>Active</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIssueTypeDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveIssueType} disabled={submitting}>
              {submitting ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Service Dialog */}
      <Dialog open={createServiceDialogOpen} onOpenChange={setCreateServiceDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              <Wrench className="w-5 h-5 inline mr-2" />
              Create Service from Complaint
            </DialogTitle>
          </DialogHeader>
          
          {selectedComplaint && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg mb-4">
              <p className="text-sm font-medium text-amber-800">
                Creating service for: {selectedComplaint.complaint_number}
              </p>
              <p className="text-xs text-amber-700">
                Customer: {selectedComplaint.customer_name} | {selectedComplaint.mobile_number}
              </p>
            </div>
          )}
          
          <div className="space-y-4">
            <div>
              <Label>Customer *</Label>
              <Select
                value={serviceForm.customer_id}
                onValueChange={(v) => setServiceForm(prev => ({ ...prev, customer_id: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select customer" />
                </SelectTrigger>
                <SelectContent>
                  {customers.map(c => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.company_name} - {c.mobile}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                Select or create customer first if not in list
              </p>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Service Type *</Label>
                <Select
                  value={serviceForm.service_type}
                  onValueChange={(v) => setServiceForm(prev => ({ ...prev, service_type: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Onsite Field Service">Onsite Field Service</SelectItem>
                    <SelectItem value="In-Office Service">In-Office Service</SelectItem>
                    <SelectItem value="Stamping Service">Stamping Service</SelectItem>
                    <SelectItem value="MMC/AMC">MMC/AMC</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Priority</Label>
                <Select
                  value={serviceForm.priority}
                  onValueChange={(v) => setServiceForm(prev => ({ ...prev, priority: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Low">Low</SelectItem>
                    <SelectItem value="Medium">Medium</SelectItem>
                    <SelectItem value="High">High</SelectItem>
                    <SelectItem value="Critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Assign To Engineer</Label>
                <Select
                  value={serviceForm.assigned_to || 'none'}
                  onValueChange={(v) => setServiceForm(prev => ({ ...prev, assigned_to: v === 'none' ? '' : v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select engineer" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Unassigned</SelectItem>
                    {engineers.map(e => (
                      <SelectItem key={e.id} value={e.id}>
                        {e.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Due Date</Label>
                <Input
                  type="date"
                  value={serviceForm.due_date}
                  onChange={(e) => setServiceForm(prev => ({ ...prev, due_date: e.target.value }))}
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>
            </div>
            
            <div>
              <Label>Issue Description *</Label>
              <Textarea
                value={serviceForm.issue_description}
                onChange={(e) => setServiceForm(prev => ({ ...prev, issue_description: e.target.value }))}
                rows={5}
                placeholder="Describe the issue..."
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateServiceDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateService} disabled={submitting}>
              {submitting ? 'Creating...' : 'Create Service'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ExportPasswordDialog {...protectedExport} />
    </div>
  );
};

export default ComplaintsManager;
